// /police-clearance-foreigner/$nationality/embassy-legalization — TH deep combo
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { FOREIGNER_NATIONALITIES, getForeignerNationality } from "@/data/police-foreigner-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { Stamp, MessageCircle, Phone, BadgeCheck, ListChecks, Globe2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/police-clearance-foreigner/$nationality/embassy-legalization")({
  loader: ({ params }) => {
    if (!getForeignerNationality(params.nationality)) throw notFound();
    return { slug: params.nationality };
  },
  head: ({ params }) => {
    const n = getForeignerNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("Not found", "/police-clearance-foreigner/$nationality/embassy-legalization", "th")}, { name: "robots", content: "noindex" }] };
    const url = `${SITE}/police-clearance-foreigner/${n.slug}/embassy-legalization`;
    const chain = n.hague ? "Apostille + MFA ไทย" : `${n.embassyBkkTh} + MFA ไทย`;
    const title = `รับรอง Police Clearance ${n.homeAuthority} โดย${chain} | NYC`;
    const desc = `รับรอง PC ${n.countryTh} แบบครบวงจร (${chain}) — สำหรับชาว${n.nameTh}ในไทย ${n.timelineDays} — คุยกับผู้เชี่ยวชาญตัวจริงได้เลยวันนี้`;
    return {
      meta: [
        { title: ctaTitle(title, "/police-clearance-foreigner/$nationality/embassy-legalization", "th") }, { name: "description", content: ctaDesc(desc, "/police-clearance-foreigner/$nationality/embassy-legalization", "th")},
        { property: "og:title", content: ctaTitle(title, "/police-clearance-foreigner/$nationality/embassy-legalization", "th")},
        { property: "og:description", content: ctaDesc(desc, "/police-clearance-foreigner/$nationality/embassy-legalization", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(
          `/police-clearance-foreigner/${n.slug}/embassy-legalization`,
          `/en/police-clearance-foreigner/${n.slug}/embassy-legalization`,
        ),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `รับรอง Police Clearance ${n.countryEn} + ${chain}`,
              serviceType: "Police Clearance Legalization",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: n.countryEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: n.priceFromTh + 2500, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: `${SITE}/` },
                { "@type": "ListItem", position: 2, name: "Police Clearance", item: `${SITE}/police-clearance` },
                { "@type": "ListItem", position: 3, name: `ชาว${n.nameTh}`, item: `${SITE}/police-clearance-foreigner/${n.slug}` },
                { "@type": "ListItem", position: 4, name: "รับรอง Legalization", item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: Page,
});

function Page() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getForeignerNationality(slug)!;
  const related = FOREIGNER_NATIONALITIES.filter((x) => x.slug !== slug).slice(0, 8);
  const price = n.priceFromTh + 2500;
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/police-clearance-foreigner" className="hover:text-gold">ชาวต่างชาติในไทย</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/police-clearance-foreigner/$nationality" params={{ nationality: slug }} className="hover:text-gold">ชาว{n.nameTh}</Link><span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">รับรอง Legalization</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Stamp className="h-3.5 w-3.5" /> {n.hague ? "Apostille" : "Embassy"} + MFA ไทย
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            รับรอง Police Clearance {n.homeAuthority} แบบครบวงจร สำหรับชาว{n.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            {n.hague
              ? `ปิด Apostille ที่ประเทศต้นทาง (${n.countryTh}) → แปลไทย → รับรองกรมการกงสุล (MFA) — ยื่นตม./อำเภอ/BOI ได้ทันที`
              : `Notary → กระทรวงต่างประเทศ ${n.countryTh} → ${n.embassyBkkTh} → แปลไทย → MFA ไทย — chain ครบสำหรับตม./อำเภอ/PR`}
            {" · "} {n.timelineDays} · เริ่ม ฿{price.toLocaleString()}
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          {n.hague
            ? `ประเทศ${n.countryTh}อยู่ใน Hague Convention — ${n.homeAuthority} สามารถปิด Apostille ที่ต้นทางแล้วส่งเข้าไทยเพื่อแปลไทย + รับรอง MFA ยื่นตม./อำเภอได้ทันที ไม่ต้องผ่านสถานทูตอีก`
            : `ประเทศ${n.countryTh}ไม่ได้อยู่ใน Hague — ต้อง Full chain: Notary → Foreign Ministry ${n.countryTh} → ${n.embassyBkkTh} → แปลไทย → MFA ไทย เพื่อให้เอกสารมีผลบังคับใช้ในไทย`}
        </blockquote>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <ListChecks className="h-6 w-6 text-amber-600" /> ขั้นตอน Chain รับรอง
          </h2>
          <ol className="mt-5 space-y-3 text-[15px]">
            {(n.hague
              ? [
                  `รับใบ ${n.homeAuthority} จาก ${n.countryEn} (name check / e-issued)`,
                  `ยื่น Apostille ที่ Competent Authority ของ ${n.countryEn}`,
                  `ส่งเอกสารกลับไทยผ่าน courier tracking`,
                  `แปลไทยโดยนักแปลขึ้นทะเบียนกรมการกงสุล`,
                  `รับรองที่กรมการกงสุล กระทรวงการต่างประเทศ (MFA) ถ.แจ้งวัฒนะ`,
                  `จัดส่งพร้อมเอกสารต้นฉบับ Apostilled + แปลรับรอง MFA`,
                ]
              : [
                  `รับใบ ${n.homeAuthority}`,
                  `Notarize ที่ Notary Public ประเทศต้นทาง`,
                  `รับรองที่ Foreign Ministry ${n.countryEn}`,
                  `รับรองที่ ${n.embassyBkkEn} ในกรุงเทพ`,
                  `แปลไทย + รับรองกรมการกงสุล (MFA ไทย)`,
                  `จัดส่ง set ครบพร้อมยื่นตม./อำเภอ/PR`,
                ]
            ).map((s, i) => (
              <li key={i} className="rounded-2xl border border-slate-200 bg-white p-4">
                <strong className="text-navy-deep">{i + 1}.</strong> {s}
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `PC ${n.countryTh} ต้อง Apostille หรือ Legalize?`, a: n.hague ? `Apostille ที่ ${n.countryEn} (ประเทศสมาชิก Hague ตั้งแต่อนุสัญญา 1961)` : `Legalization full-chain: Notary → Foreign Ministry ${n.countryEn} → ${n.embassyBkkTh}` },
                { q: "ต้องแปลไทย + MFA ทุกครั้งไหม?", a: "ใช่ — ไม่ว่าจะ Apostille หรือ Legalize มาแล้ว ไทยยังต้องแปลไทย + รับรองกรมการกงสุล (MFA) ก่อนยื่นราชการไทย" },
                { q: "ระยะเวลารวมทั้ง chain?", a: `${n.timelineDays} (รวมทุกขั้นตอน)` },
                { q: "ราคารวมทุกอย่าง?", a: `เริ่ม ฿${price.toLocaleString()} — รวม PC + ${n.hague ? "Apostille" : "Legalization"} + แปลไทย + MFA` },
                { q: "อายุใช้งานของเอกสาร?", a: "ตม./อำเภอส่วนใหญ่รับ PC ที่ออกไม่เกิน 3 เดือน — Apostille/Legalize/MFA ต้องดำเนินการภายในกรอบนี้" },
              ]}
            />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Globe2 className="h-6 w-6 text-amber-600" /> รับรอง Legalization สัญชาติอื่น
          </h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/police-clearance-foreigner/$nationality/embassy-legalization" params={{ nationality: r.slug }}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:border-gold/50">
                <span className="font-semibold">{r.homeAuthority}</span>
                <span className="ml-auto text-[10px] text-slate-500">{r.hague ? "Apostille" : "Legalize"}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
      <QuotableStatsStrip cluster="police" locale="th" variant="light" />
    </main>
  );
}
